import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useSesion } from '@/modules/auth/useSesion'
import { useOpcionesServicios } from '@/shared/datos/useOpciones'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { SinDatos } from '@/shared/ui/Estados/SinDatos'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'
import { normalizar } from '@/shared/utilidades/formato'

import { FormularioNuevaOrden } from './FormularioNuevaOrden'
import { MapaOrdenes } from './MapaOrdenes'
import { TableroRecorrido } from './TableroRecorrido'
import { useOrdenesServicio } from './useOrdenesServicio'
import { FILTROS_INICIALES } from './tipos'
import estilos from './MatrizOrdenes.module.css'

/**
 * La matriz de órdenes de servicio.
 *
 * Es la pantalla donde Camilo mete el trabajo antes de que exista precio y
 * donde ve, de un vistazo, cuánto hay esperando cotización. El recuento de
 * arriba no es decorativo: "Pendiente de cotizar" es el trabajo que ya está
 * contado pero todavía no vale nada, y es lo primero que hay que mover.
 *
 * Una sola vista: el RECORRIDO —en qué estado está cada orden, dónde se atasca
 * la cadena— con el MAPA debajo, en la misma pantalla, contestando dónde queda.
 * El reparto (quién y a qué hora) vive en el Tablero y en la ficha de cada
 * orden: tenerlo también aquí eran tres caminos para lo mismo, tres sitios donde
 * la misma regla se iba separando.
 */
export function MatrizOrdenes() {
  const navegar = useNavigate()
  const { puede } = useSesion()

  const { ordenes, cargando, error } = useOrdenesServicio()
  const { opciones: servicios } = useOpcionesServicios()

  // La campana de avisos abre «/ordenes?buscar=OT-…». Sin leerlo la persona
  // aterrizaba en la matriz completa y tenía que volver a filtrar a mano.
  const [params] = useSearchParams()
  const [filtros, setFiltros] = useState(() => ({
    ...FILTROS_INICIALES,
    busqueda: params.get('buscar') ?? FILTROS_INICIALES.busqueda,
  }))
  const [creando, setCreando] = useState(false)

  // Si la matriz ya estaba montada (se toca la campana estando en /ordenes), el
  // inicializador de arriba no se vuelve a ejecutar: hay que sembrar el filtro
  // cuando cambian los parámetros de la URL.
  useEffect(() => {
    const buscar = params.get('buscar')
    if (buscar === null) return
    setFiltros((previos) => ({
      ...previos,
      busqueda: buscar,
    }))
  }, [params])

  const puedeCrear = puede('ordenes_servicio', 'crear')

  const filtradas = useMemo(() => {
    const termino = normalizar(filtros.busqueda)

    return ordenes.filter((orden) => {
      if (filtros.estado !== 'todos' && orden.estado !== filtros.estado) return false
      if (filtros.servicioId !== 'todos' && orden.servicio_id !== filtros.servicioId) return false

      if (!termino) return true

      // Se busca por lo que la gente recuerda: el número que dictó por
      // teléfono, el nombre del cliente o la dirección donde fue el trabajo.
      const texto = [
        orden.numero,
        // El número de la orden de trabajo: es con el que llega la campana de
        // avisos (OT-…), que antes no encontraba nada aquí.
        orden.orden_trabajo?.numero,
        orden.cliente?.nombre,
        orden.cliente?.documento,
        orden.direccion,
        orden.cotizacion?.numero_cotizacion,
      ]
        .filter(Boolean)
        .join(' ')

      return normalizar(texto).includes(termino)
    })
  }, [ordenes, filtros])

  return (
    <>
      {/* Sin título ni subtítulo: la barra de arriba ya dice «Matriz de
          órdenes», y repetirlo con una frase explicativa debajo gasta
          cuarenta píxeles de alto en la pantalla que más se mira al día. La
          cabecera se queda solo con los mandos, que sí hacen algo. */}
      <Tarjeta
        sinRelleno
        /* El recorrido lleva el kanban y, debajo, el mapa a lo ancho: la
           tarjeta llega hasta el borde de la ventana. */
        estira
        acciones={
          <div className={estilos.filtros}>
            <input
              className={estilos.buscador}
              type="search"
              placeholder="Número, cliente o dirección…"
              value={filtros.busqueda}
              onChange={(e) => setFiltros((previos) => ({ ...previos, busqueda: e.target.value }))}
              aria-label="Buscar en la matriz"
            />

            <select
              className={estilos.selectorFiltro}
              value={filtros.servicioId}
              onChange={(e) =>
                setFiltros((previos) => ({ ...previos, servicioId: e.target.value }))
              }
              aria-label="Filtrar por servicio"
            >
              <option value="todos">Todos los servicios</option>
              {servicios.map((servicio) => (
                <option key={servicio.valor} value={servicio.valor}>
                  {servicio.etiqueta}
                </option>
              ))}
            </select>

            {/* La cuenta va aquí y no al pie: es la respuesta a los filtros
                que están al lado, y ochocientos píxeles más abajo nadie la
                relaciona con lo que acaba de escribir. */}
            <span className={estilos.cuenta}>
              {filtradas.length === ordenes.length ? (
                <>
                  <b>{ordenes.length}</b> órdenes
                </>
              ) : (
                <>
                  <b>{filtradas.length}</b> de {ordenes.length}
                </>
              )}
            </span>

            {puedeCrear && (
              <Boton variante="principal" onClick={() => setCreando(true)}>
                Nueva orden
              </Boton>
            )}
          </div>
        }
      >
        {cargando ? (
          <Cargando mensaje="PROCESANDO ÓRDENES SERVICIO..." />
        ) : error ? (
          <div style={{ padding: 'var(--e-5)' }}>
            <Aviso tipo="error" titulo="No se pudo cargar la matriz">
              {error}
            </Aviso>
          </div>
        ) : (
          <div style={{ padding: 'var(--e-4)' }}>
            {filtradas.length === 0 ? (
              <SinDatos
                titulo={
                  ordenes.length === 0 ? 'Todavía no hay órdenes de servicio' : 'Ningún resultado'
                }
                mensaje={
                  ordenes.length === 0
                    ? 'La primera orden se crea con el botón «Nueva orden». No hace falta que tenga precio todavía.'
                    : 'Pruebe con otros filtros o con otro término de búsqueda.'
                }
              />
            ) : (
              <>
                <TableroRecorrido ordenes={filtradas} />

                {/* EL MAPA, DEBAJO DEL KANBAN Y EN LA MISMA PANTALLA.
                    El recorrido contesta «en qué va cada trabajo» y el mapa
                    «dónde está»; son la misma pregunta partida en dos, y
                    tenerlas en pestañas distintas obligaba a recordar una
                    mientras se mira la otra. Juntas, una columna con seis
                    tarjetas se lee y se ve al mismo tiempo dónde caen.

                    Hereda las mismas filtradas que el kanban: filtrar arriba y
                    ver el mapa entero haría que las dos mitades de la misma
                    pantalla contaran cosas distintas. */}
                <div className={estilos.mapaBajoKanban}>
                  {/* Sin `onProgramar`: aquí el mapa solo se mira. Programar
                      vive en el Tablero, que tiene el eje del tiempo, y en la
                      ficha de cada orden. */}
                  <MapaOrdenes ordenes={filtradas} onAbrir={(id) => navegar(`/ordenes/${id}`)} />
                </div>
              </>
            )}
          </div>
        )}
      </Tarjeta>

      <FormularioNuevaOrden abierto={creando} onCerrar={() => setCreando(false)} />
    </>
  )
}
